import { useState, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Building2, Globe, MapPin, Users, Briefcase, FileText, 
  DollarSign, Play, ExternalLink, ChevronLeft, ChevronRight,
  CheckCircle, X, Mail, Phone, Linkedin, Calendar, TrendingUp
} from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Helper to construct correct deck URL regardless of stored format
const getDeckUrl = (deckUrl) => {
  if (!deckUrl) return null;
  
  // If it's already an S3 URL or other absolute URL, use it directly
  if (deckUrl.startsWith('https://') && (deckUrl.includes('.s3.') || deckUrl.includes('amazonaws.com'))) {
    return deckUrl;
  }
  
  let deckPath = deckUrl;
  // If it's a full URL from old deployment, extract just the path
  if (deckPath.startsWith('http')) {
    try {
      const url = new URL(deckPath);
      deckPath = url.pathname;
    } catch (e) {
      // Fallback: extract path after domain
      deckPath = '/' + deckPath.split('/').slice(3).join('/');
    }
  }
  // Ensure path uses /api/uploads for ingress routing
  if (deckPath.startsWith('/uploads')) {
    deckPath = deckPath.replace('/uploads', '/api/uploads');
  } else if (!deckPath.startsWith('/api/uploads')) {
    deckPath = '/api/uploads/' + deckPath.split('/').pop();
  }
  return `${process.env.REACT_APP_BACKEND_URL}${deckPath}`;
};

// Helper to detect media type from URL
const getMediaType = (url) => {
  if (!url) return null;
  
  const urlLower = url.toLowerCase();
  
  // YouTube patterns
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
    return 'youtube';
  }
  
  // Google Drive patterns  
  if (urlLower.includes('drive.google.com')) {
    return 'google-drive';
  }
  
  // Loom patterns
  if (urlLower.includes('loom.com')) {
    return 'loom';
  }
  
  // Vimeo patterns
  if (urlLower.includes('vimeo.com')) {
    return 'vimeo';
  }
  
  return 'link';
};

// Extract YouTube video ID
const getYouTubeEmbedUrl = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/v\/([^&\s?]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return null;
};

// Extract Google Drive embed URL
const getGoogleDriveEmbedUrl = (url) => {
  // Handle /file/d/FILE_ID/view format
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (fileMatch) {
    return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;
  }
  
  // Handle /open?id=FILE_ID format
  const openMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (openMatch) {
    return `https://drive.google.com/file/d/${openMatch[1]}/preview`;
  }
  
  return null;
};

// Extract Loom embed URL
const getLoomEmbedUrl = (url) => {
  const match = url.match(/loom\.com\/share\/([^?]+)/);
  if (match) {
    return `https://www.loom.com/embed/${match[1]}`;
  }
  return null;
};

// Extract Vimeo embed URL
const getVimeoEmbedUrl = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  if (match) {
    return `https://player.vimeo.com/video/${match[1]}`;
  }
  return null;
};

// Media Preview Component
const MediaPreview = ({ url, title }) => {
  const mediaType = getMediaType(url);
  
  if (!url) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <Play className="h-12 w-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500">No demo available</p>
      </div>
    );
  }
  
  let embedUrl = null;
  
  switch (mediaType) {
    case 'youtube':
      embedUrl = getYouTubeEmbedUrl(url);
      break;
    case 'google-drive':
      embedUrl = getGoogleDriveEmbedUrl(url);
      break;
    case 'loom':
      embedUrl = getLoomEmbedUrl(url);
      break;
    case 'vimeo':
      embedUrl = getVimeoEmbedUrl(url);
      break;
    default:
      break;
  }
  
  if (embedUrl) {
    return (
      <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg bg-black">
        <iframe
          src={embedUrl}
          title={title || 'Demo Video'}
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  
  // Fallback for non-embeddable links
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 text-center border border-blue-100">
      <ExternalLink className="h-10 w-10 text-blue-500 mx-auto mb-3" />
      <p className="text-gray-700 font-medium mb-3">External Demo Link</p>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Open Demo <ExternalLink className="h-4 w-4" />
      </a>
    </div>
  );
};

// PDF Viewer Component
const PDFViewer = ({ url }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadError, setLoadError] = useState(false);
  
  // URL is already processed by getDeckUrl before being passed in
  const fullUrl = url || '';
  
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoadError(false);
  };
  
  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setLoadError(true);
  };
  
  if (!url) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-500">No pitch deck uploaded</p>
      </div>
    );
  }
  
  // Check if it's a PPT file
  if (url.toLowerCase().endsWith('.ppt') || url.toLowerCase().endsWith('.pptx')) {
    return (
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-6 text-center border border-orange-100">
        <FileText className="h-10 w-10 text-orange-500 mx-auto mb-3" />
        <p className="text-gray-700 font-medium mb-1">PowerPoint Presentation</p>
        <p className="text-sm text-gray-500 mb-4">PPT files cannot be previewed inline</p>
        <a 
          href={fullUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          Download Deck <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    );
  }
  
  if (loadError) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-6 text-center border border-red-100">
        <FileText className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <p className="text-gray-700 font-medium mb-1">Unable to preview PDF</p>
        <p className="text-sm text-gray-500 mb-4">The PDF could not be loaded inline</p>
        <a 
          href={fullUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Open PDF <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden shadow-lg">
      {/* PDF Controls */}
      <div className="flex items-center justify-between bg-gray-800 px-4 py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
            className="text-white hover:bg-gray-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-white text-sm">
            Page {pageNumber} of {numPages || '?'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPageNumber(p => Math.min(numPages || 1, p + 1))}
            disabled={pageNumber >= (numPages || 1)}
            className="text-white hover:bg-gray-700"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <a 
          href={fullUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
        >
          Open full <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      
      {/* PDF Document */}
      <div className="flex justify-center p-4 bg-gray-800 min-h-[400px]">
        <Document
          file={fullUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center h-[400px]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white"></div>
            </div>
          }
        >
          <Page 
            pageNumber={pageNumber} 
            width={550}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
};

// Info Item Component
const InfoItem = ({ icon: Icon, label, value, href, badge }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-blue-600" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium truncate block">
          {value}
        </a>
      ) : badge ? (
        <Badge className={badge.className}>{value}</Badge>
      ) : (
        <p className="font-medium text-gray-900 truncate">{value || '-'}</p>
      )}
    </div>
  </div>
);

// Founder Card Component  
const FounderCard = ({ founder, index }) => (
  <Card className="border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
          {founder.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{founder.name}</p>
          <p className="text-xs text-gray-500">{index === 0 ? 'Founder' : `Co-founder`}</p>
          <div className="mt-2 space-y-1">
            <a href={`mailto:${founder.email}`} className="flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600">
              <Mail className="h-3 w-3" /> {founder.email}
            </a>
            <p className="flex items-center gap-1 text-sm text-gray-600">
              <Phone className="h-3 w-3" /> {founder.phone}
            </p>
            <a href={founder.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
              <Linkedin className="h-3 w-3" /> LinkedIn Profile
            </a>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Revenue Chart Component
const RevenueChart = ({ revenue }) => {
  const months = [1, 2, 3, 4, 5];
  const values = months.map(i => revenue?.[`month${i}`] || 0);
  const maxValue = Math.max(...values, 1);
  
  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
      <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-green-600" />
        Revenue (Last 5 Months)
      </h4>
      <div className="flex items-end justify-between gap-2 h-32">
        {months.map((i) => {
          const value = revenue?.[`month${i}`] || 0;
          const height = (value / maxValue) * 100;
          const label = revenue?.[`month${i}_label`] || `M${i}`;
          
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="relative w-full flex justify-center">
                <div 
                  className="w-8 bg-gradient-to-t from-green-600 to-green-400 rounded-t-md transition-all duration-300"
                  style={{ height: `${Math.max(height, 4)}px` }}
                />
              </div>
              <p className="text-[10px] text-gray-500 font-medium">{label}</p>
              <p className="text-xs font-semibold text-gray-700">₹{value.toLocaleString('en-IN')}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Dialog Component
export default function ApplicationDetailsDialog({ 
  open, 
  onOpenChange, 
  application, 
  onApprove, 
  onReject 
}) {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!application) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-white mb-1">
                  {application.brand_name}
                </DialogTitle>
                <p className="text-blue-100 text-sm">{application.legal_name}</p>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-white/20 text-white border-0">
                  {application.stage}
                </Badge>
                <Badge className="bg-white/20 text-white border-0">
                  {application.category}
                </Badge>
              </div>
            </div>
            <p className="mt-3 text-lg text-blue-50 italic">&ldquo;{application.one_liner}&rdquo;</p>
          </DialogHeader>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <div className="border-b bg-gray-50 px-6">
            <TabsList className="bg-transparent h-12 p-0 gap-4">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-t-lg px-4 py-2 border-b-2 border-transparent data-[state=active]:border-blue-600"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger 
                value="pitch" 
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-t-lg px-4 py-2 border-b-2 border-transparent data-[state=active]:border-blue-600"
              >
                Pitch Deck
              </TabsTrigger>
              <TabsTrigger 
                value="demo" 
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-t-lg px-4 py-2 border-b-2 border-transparent data-[state=active]:border-blue-600"
              >
                Demo
              </TabsTrigger>
            </TabsList>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(95vh-280px)] p-6">
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0 space-y-6">
              {/* Company Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoItem icon={Building2} label="Legal Name" value={application.legal_name} />
                <InfoItem icon={Globe} label="Website" value={application.website} href={application.website} />
                <InfoItem icon={MapPin} label="Location" value={application.location} />
                <InfoItem 
                  icon={Calendar} 
                  label="Submitted" 
                  value={new Date(application.created_at).toLocaleDateString()} 
                />
              </div>
              
              {/* Founders */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" /> Founders ({application.founders?.length || 0})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {application.founders?.map((founder, idx) => (
                    <FounderCard key={idx} founder={founder} index={idx} />
                  ))}
                </div>
              </div>
              
              {/* Description & Product */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-purple-600" /> Description
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{application.description}</p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" /> Product
                    </h4>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{application.product}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Product Pricing */}
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-amber-600" /> Product Pricing
                  </h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{application.product_pricing}</p>
                </CardContent>
              </Card>
              
              {/* Revenue Chart - COMMENTED OUT FOR LATER
              <RevenueChart revenue={application.revenue} />
              */}
              
              {/* Fundraising */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                  Fundraising
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Amount to Raise</p>
                    <p className="text-2xl font-bold text-purple-700">
                      ₹{application.amount_to_raise?.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Terms Type</p>
                    <Badge className="bg-purple-100 text-purple-700 mt-1">{application.terms_type}</Badge>
                    {application.terms_type === 'Valuation' && (
                      <p className="text-sm text-gray-600 mt-1">
                        Valuation: ₹{application.valuation_amount?.toLocaleString('en-IN')}
                      </p>
                    )}
                    {application.terms_type === 'SAFE' && (
                      <p className="text-sm text-gray-600 mt-1">{application.safe_terms}</p>
                    )}
                    {application.terms_type === 'Other' && (
                      <p className="text-sm text-gray-600 mt-1">{application.other_terms}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Custom Fields (Deal-Specific) */}
              {application.custom_fields && application.custom_fields.length > 0 && (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-600" />
                    Additional Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {application.custom_fields.map((field, idx) => (
                      <div key={field.id || idx}>
                        <p className="text-xs text-gray-500 uppercase">{field.label}</p>
                        {field.type === 'url' && field.value ? (
                          <a 
                            href={field.value} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline break-all"
                          >
                            {field.value}
                          </a>
                        ) : field.type === 'textarea' ? (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{field.value || '-'}</p>
                        ) : (
                          <p className="text-sm font-medium text-gray-700">{field.value || '-'}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            {/* Pitch Deck Tab */}
            <TabsContent value="pitch" className="mt-0">
              <PDFViewer url={getDeckUrl(application.deck_url)} />
            </TabsContent>
            
            {/* Demo Tab */}
            <TabsContent value="demo" className="mt-0">
              <MediaPreview url={application.demo_link} title={`${application.brand_name} Demo`} />
            </TabsContent>
          </div>
        </Tabs>
        
        {/* Footer Actions */}
        <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Application ID: {application.id?.slice(0, 8)}...
          </p>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="border-gray-300"
            >
              Close
            </Button>
            <Button 
              variant="outline" 
              onClick={() => onReject(application)}
              className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
            >
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
            <Button 
              onClick={() => onApprove(application)}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle className="h-4 w-4 mr-1" /> Accept
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
